import type { JSX, Ref } from 'react'

import {
  Button,
  Card,
  CheckIcon,
  Dialog,
  List,
  Notice,
  Text,
} from '@buffer-mono/popcorn'

import { PublicShareNowArt } from './PublicShareNowArt'

import styles from './PublicShareNowFork.module.css'

const ACCOUNT_FEATURES = [
  'Scheduling for later',
  'Posting to multiple channels at once',
  'Tracking performance and replying to comments in one place',
] as const

const MANUAL_STEPS = [
  'Publish it immediately',
  'Share to each channel one by one',
  'Track performance and reply to comments within each platform',
] as const

export function PublicShareNowFork({
  titleRef,
  hasAccountHandoffError,
  onCreateAccount,
  onShareManually,
}: {
  titleRef: Ref<HTMLHeadingElement>
  hasAccountHandoffError: boolean
  onCreateAccount: () => void
  onShareManually: () => void
}): JSX.Element {
  return (
    <>
      <Dialog.Header className={styles.header}>
        <Dialog.Title ref={titleRef} tabIndex={-1} className={styles.title}>
          How do you want to send this?
        </Dialog.Title>
      </Dialog.Header>
      <Dialog.Body className={styles.body}>
        {hasAccountHandoffError ? (
          <Notice variant="error">
            <Notice.Text>
              We couldn’t prepare this draft for transfer. It remains in this
              browser. Try again.
            </Notice.Text>
          </Notice>
        ) : null}
        <div className={styles.paths}>
          <Card
            asChild
            padding="none"
            className={`${styles.path} ${styles.accountPath}`}
          >
            <section aria-labelledby="share-now-account-path">
              <div className={styles.art}>
                <PublicShareNowArt variant="buffer" />
              </div>
              <div className={styles.pathBody}>
                <div className={styles.pathHead}>
                  <Text
                    as="h4"
                    id="share-now-account-path"
                    size="lg"
                    weight="medium"
                  >
                    Let Buffer post it
                  </Text>
                  <Text as="p" color="subtle">
                    It’s free. Connect your channels once, and share in a single
                    click. Also included:
                  </Text>
                </div>
                <div className={styles.points}>
                  <List className={styles.pointList}>
                    {ACCOUNT_FEATURES.map((feature) => (
                      <List.Item key={feature} icon={CheckIcon}>
                        {feature}
                      </List.Item>
                    ))}
                  </List>
                </div>
                <Button className={styles.pathAction} onClick={onCreateAccount}>
                  Create account
                </Button>
              </div>
            </section>
          </Card>
          <Card asChild padding="none" className={styles.path}>
            <section aria-labelledby="share-now-manual-path">
              <div className={styles.art}>
                <PublicShareNowArt variant="manual" />
              </div>
              <div className={styles.pathBody}>
                <div className={styles.pathHead}>
                  <Text
                    as="h4"
                    id="share-now-manual-path"
                    size="lg"
                    weight="medium"
                  >
                    Post it yourself
                  </Text>
                  <Text as="p" color="subtle">
                    No account needed. Copy your post to:
                  </Text>
                </div>
                <div className={styles.points}>
                  <List className={styles.pointList}>
                    {MANUAL_STEPS.map((step) => (
                      <List.Item key={step}>{step}</List.Item>
                    ))}
                  </List>
                </div>
                <Button
                  variant="secondary"
                  className={styles.pathAction}
                  onClick={onShareManually}
                >
                  Share it manually
                </Button>
              </div>
            </section>
          </Card>
        </div>
      </Dialog.Body>
    </>
  )
}
